
// src/components/PromotionPickerDialog.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, RadioGroup, FormControlLabel, Radio,
  List, ListItemButton, ListItemText, Chip, Box, Stack, Typography
} from "@mui/material";

const API = process.env.REACT_APP_API_BASE ?? "";

// small helper
const inRangeNow = (from, to) => {
  const now = new Date();
  const f = from ? new Date(from) : null;
  const t = to ? new Date(to) : null;
  if (f && now < f) return false;
  if (t && now > t) return false;
  return true;
};

export default function PromotionPickerDialog({
  open,
  onClose,
  cinemaId,
  movieId,
  onPick,
  initialScope = "all", // "all" | "tickets" | "bar"
}) {
  const [loading, setLoading] = useState(false);
  const [promos, setPromos] = useState([]);
  const [q, setQ] = useState("");
  const [scope, setScope] = useState(initialScope);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    fetch(`${API}/api/promotions`, { credentials: "include" })
      .then(r => r.json())
      .then((rows) => setPromos(Array.isArray(rows) ? rows : []))
      .catch(() => setPromos([]))
      .finally(() => setLoading(false));
  }, [open]);

  // filter: active, date window, applies to cinema, (optionally) movie
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return promos.filter(p => {
      if (!p.active) return false;
      if (!inRangeNow(p.valid_from, p.valid_to)) return false;
      const cinemaOk = p.apply_all_cinemas || (Array.isArray(p.cinema_ids) && p.cinema_ids.includes(Number(cinemaId)));
      if (!cinemaOk) return false;
      const movieOk = !movieId || p.apply_all_movies || (Array.isArray(p.movie_ids) && p.movie_ids.includes(Number(movieId)));
      if (!movieOk) return false;
      if (!needle) return true;
      const s = `${p.code || ""} ${p.name || ""}`.toLowerCase();
      return s.includes(needle);
    }).sort((a,b) => String(a.code).localeCompare(String(b.code)));
  }, [promos, q, cinemaId, movieId]);

  const handleConfirm = () => {
    if (!selected) return onClose?.();
    onPick?.({ ...selected, scope });
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Промоции</DialogTitle>
      <DialogContent dividers>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <TextField
            fullWidth
            size="small"
            label="Търси по код/име"
            value={q}
            onChange={(e)=>setQ(e.target.value)}
          />
          <RadioGroup
            value={scope}
            onChange={(e)=>setScope(e.target.value)}
            row
          >
            <FormControlLabel value="all" control={<Radio />} label="Бар+Билети" />
            <FormControlLabel value="tickets" control={<Radio />} label="Само билети" />
            <FormControlLabel value="bar" control={<Radio />} label="Само бар" />
          </RadioGroup>
        </Stack>

        <List dense sx={{ maxHeight: 360, overflowY: "auto" }}>
          {filtered.map(p => {
            const subtitle = [];
            const amt = Number(p.amount || 0);
            if (String(p.type || "").toLowerCase() === "bundle") {
              subtitle.push(`Пакет: ${amt.toFixed(2)} лв`);
            } else if (String(p.type || "").toLowerCase() === "fixed") {
              subtitle.push(`−${amt.toFixed(2)} лв`);
            } else {
              // percent or free-bundle style (amount may be 0)
              subtitle.push(amt > 0 ? `−${amt}%` : (p.bundle_bar_items?.length ? "Подарък / безплатно" : "Промо"));
            }
            if (!p.apply_all_cinemas) subtitle.push(`кина: ${p.cinema_ids?.length||0}`);
            if (!p.apply_all_movies)  subtitle.push(`филми: ${p.movie_ids?.length||0}`);
            return (
              <ListItemButton
                key={p.promo_id}
                selected={selected?.promo_id === p.promo_id}
                onClick={()=>setSelected(p)}
              >
                <ListItemText
                  primary={<Stack direction="row" spacing={1} alignItems="center">
                    <Typography sx={{ fontWeight: 700 }}>{p.code}</Typography>
                    {p.name && <Chip size="small" label={p.name} />}
                  </Stack>}
                  secondary={subtitle.join(" • ")}
                />
              </ListItemButton>
            );
          })}
          {!loading && filtered.length === 0 && (
            <Box sx={{ p: 2, opacity: 0.6 }}>
              <em>Няма активни промоции за това кино.</em>
            </Box>
          )}
        </List>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Затвори</Button>
        <Button onClick={handleConfirm} variant="contained" disabled={!selected}>Добави</Button>
      </DialogActions>
    </Dialog>
  );
}
